import { useEffect, useState } from "react";
import useFetch from "./useFetch";
import usePost from "./usePost";
import useCurrent from "./useCurrent";

const useLicense = (withCreate = true) => {
    const { currentTenantId: tenantId } = useCurrent();
    
    // ✅ عدم الجلب إذا لم يكن tenantId موجود
    const shouldFetch = tenantId && tenantId !== 'undefined' && tenantId !== 'null';
    const apiUrl = shouldFetch 
        ? `${process.env.REACT_APP_Auth_API_BASE_URL}/licenses/tenant/${tenantId}`
        : null;
    
    const { data, loading, error } = useFetch(apiUrl);
    const { data: resultPost, postData, error: postError } = usePost();
    const [isCreated, setIsCreated] = useState(false);

    const TRAIL_PERIOD_DAYS = 14;
    const [licenseDays, setLicenseDays] = useState(TRAIL_PERIOD_DAYS);
    const [typeLicense, setTypeLicense] = useState('Trail');

    const createLicense = () => {
        // ✅ التحقق من tenantId قبل إنشاء License
        if (!tenantId || tenantId === 'undefined' || tenantId === 'null') {
            console.error('Cannot create license: Invalid tenantId', tenantId);
            return;
        }

        // ✅ منع إنشاء license مكررة
        if (isCreated) {
            console.log('License creation already in progress');
            return;
        }

        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() + TRAIL_PERIOD_DAYS);

        const _data = {
            tenantId: tenantId,
            companiesCount: 1,
            branchesCount: 1,
            usersCount: 1,
            appIds: ['SALES'],
            typeLicense: 'Trail',
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
        };

        console.log('Creating license with data:', _data);
        postData(`${process.env.REACT_APP_Auth_API_BASE_URL}/licenses`, _data);
        setIsCreated(true);
    };

    // ✅ معالجة نتيجة إنشاء License
    useEffect(() => {
        if (resultPost) {
            console.log('License Created Successfully:', resultPost);
            // يمكنك إعادة تحميل البيانات هنا إذا لزم الأمر
        }
    }, [resultPost]);

    // ✅ معالجة خطأ إنشاء License (403)
    useEffect(() => {
        if (postError) {
            console.error('Error creating license:', postError);
            
            // إذا كان الخطأ 403، أظهر رسالة للمستخدم
            if (postError.response?.status === 403) {
                console.error('Permission denied: You do not have access to create licenses');
                // يمكنك إضافة notification هنا
            }
            
            setIsCreated(false); // إعادة تعيين لإتاحة المحاولة مرة أخرى
        }
    }, [postError]);

    // ✅ معالجة بيانات License
    useEffect(() => {
        // عدم المتابعة إذا لم يكن tenantId صالح
        if (!shouldFetch) {
            return;
        }

        // عدم المتابعة أثناء التحميل أو وجود خطأ
        if (loading) {
            return;
        }

        // ✅ التحقق الآمن من البيانات
        if (!data || !data.data) {
            console.log('No license data available yet');
            return;
        }

        // ✅ التحقق من أن data.data هو array
        if (!Array.isArray(data.data)) {
            console.error('Invalid license data format:', data);
            return;
        }

        // إذا لم توجد رخص وlicenseCreation مسموح
        if (data.data.length === 0) {
            if (!isCreated && withCreate) {
                console.log('No licenses found, creating new one...');
                createLicense();
            }
            return;
        }

        // ✅ معالجة الرخصة الموجودة بأمان
        const firstLicense = data.data[0];
        
        if (!firstLicense) {
            console.error('First license is undefined');
            return;
        }

        // تحديث نوع الرخصة
        const licenseType = firstLicense.typeLicense || 'Trail';
        setTypeLicense(licenseType);

        // ✅ حساب الأيام المتبقية بأمان
        if (firstLicense.endDate) {
            try {
                const endDate = new Date(firstLicense.endDate);
                const currentDate = new Date();
                
                // التحقق من صحة التاريخ
                if (isNaN(endDate.getTime())) {
                    console.error('Invalid endDate format:', firstLicense.endDate);
                    setLicenseDays(0);
                    return;
                }

                // حساب الفرق
                const timeDiff = endDate - currentDate;
                const daysDiff = Math.ceil(timeDiff / 86400000);
                
                // إذا كانت الرخصة منتهية
                if (daysDiff < 0) {
                    console.warn('License has expired');
                    setLicenseDays(0);
                } else {
                    setLicenseDays(daysDiff);
                }
                
                console.log(`License days remaining: ${daysDiff}`);
            } catch (error) {
                console.error('Error calculating license days:', error);
                setLicenseDays(0);
            }
        } else {
            console.warn('License endDate is missing');
            setLicenseDays(0);
        }
    }, [data, loading, shouldFetch, isCreated, withCreate, tenantId]);

    return { 
        data, 
        loading, 
        licenseDays, 
        TRAIL_PERIOD_DAYS,
        typeLicense,
        isCreated,
        error: error || postError
    };
};

export default useLicense;